import { useCallback, useEffect, useRef, useState } from 'react'
import type { ConnectionOpen, Event, StreamChat } from 'stream-chat'

import { useChatRuntimeConfig } from '../../runtime/chat-runtime-config'
import type { IChatCurrentUser } from '../../runtime/types'

type TConnectionStatus = 'connected' | 'connecting' | 'disconnected' | 'error'

interface IChatConnectionState {
  connectionStatus: TConnectionStatus
  unreadCount: number
  userConnection: ConnectionOpen | null
}

interface IChatConnectionReturn extends IChatConnectionState {
  connectUser: () => Promise<void>
  disconnectUser: () => void
}

const CHAT_CONNECTION_CONSTANTS = {
  MAX_BACKOFF_TIME: 30000,
  MAX_RECONNECT_ATTEMPTS: 10
} as const

/**
 * Manages the full Stream Chat connection lifecycle for a single authenticated user:
 * - Connects the user to Stream Chat once both `userChatToken` and `userDetails` are available
 * - Reconnects automatically with exponential backoff on `connection.changed` (offline) or
 *   `connection.error` events, up to MAX_RECONNECT_ATTEMPTS
 * - Tracks unread message count from incoming Stream events
 * - Exposes `disconnectUser` for explicit sign-out / workspace switching
 *
 * Refs are used for values that are read inside stable event-handler callbacks to avoid
 * stale closures without forcing the handlers to be recreated on every render.
 */
export function useChatConnection(
  chatClient: StreamChat,
  userDetails: IChatCurrentUser | null | undefined,
  userChatToken: string | null
): IChatConnectionReturn {
  const { telemetry } = useChatRuntimeConfig()
  const [state, setState] = useState<IChatConnectionState>({
    connectionStatus: 'disconnected',
    unreadCount: 0,
    userConnection: null
  })

  // Refs mirror the latest state values so event handlers registered once always
  // read the current status without being re-created on each state change.
  const connectionStatusRef = useRef<TConnectionStatus>('disconnected')
  const userConnectionRef = useRef<ConnectionOpen | null>(null)
  const reconnectAttempts = useRef(0)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Mutable ref to the latest `connectUser` callback, used by `scheduleReconnect`
  // so the retry timeout always calls the most-recent version of the function.
  const connectUserRef = useRef<() => Promise<void>>(() => Promise.resolve())

  /**
   * Merges partial state updates and keeps the corresponding refs in sync.
   * Using a functional setState ensures updates are batched correctly and
   * refs always reflect the committed value.
   */
  const updateState = useCallback((updates: Partial<IChatConnectionState>) => {
    setState((prev) => {
      const next = { ...prev, ...updates }
      if (updates.connectionStatus !== undefined) {
        connectionStatusRef.current = updates.connectionStatus
      }
      if (updates.userConnection !== undefined) {
        userConnectionRef.current = updates.userConnection
      }
      return next
    })
  }, [])

  /**
   * Centralized connection error handler: sets status to 'error' and reports through
   * the host-provided telemetry adapter
   * with enough context (userId, reconnect attempt count, call site) to triage
   * connection failures in production.
   */
  const handleConnectionError = useCallback(
    (context: string, error: unknown) => {
      updateState({ connectionStatus: 'error' })
      telemetry.captureException?.(error, {
        extra: {
          reconnectAttempts: reconnectAttempts.current,
          userDetails: { id: userDetails?.id, name: userDetails?.name }
        },
        tags: {
          component: 'ChatProvider',
          context,
          userId: userDetails?.id || 'unknown'
        }
      })
    },
    [telemetry, userDetails, updateState]
  )

  /**
   * Clears the pending reconnect timer and resets the attempt counter.
   * Called after a successful (re)connection or an explicit disconnect.
   */
  const resetReconnectAttempts = useCallback(() => {
    reconnectAttempts.current = 0
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
  }, [])

  /**
   * Schedules a reconnection attempt using exponential backoff:
   *   delay = min(2^attempt * 1000ms, MAX_BACKOFF_TIME)
   *
   * Bails out silently once MAX_RECONNECT_ATTEMPTS is reached and captures a
   * telemetry message so hosts can alert without throwing.
   */
  const scheduleReconnect = useCallback(() => {
    if (reconnectAttempts.current >= CHAT_CONNECTION_CONSTANTS.MAX_RECONNECT_ATTEMPTS) {
      telemetry.captureMessage?.('Maximum Stream Chat reconnection attempts reached', {
        level: 'error',
        tags: { component: 'ChatProvider', userId: userDetails?.id || 'unknown' }
      })
      return
    }

    reconnectAttempts.current += 1
    const backoffTime = Math.min(
      Math.pow(2, reconnectAttempts.current) * 1000,
      CHAT_CONNECTION_CONSTANTS.MAX_BACKOFF_TIME
    )

    telemetry.addBreadcrumb?.({
      category: 'chat',
      data: { backoffTime },
      level: 'info',
      message: `Scheduling reconnection attempt ${reconnectAttempts.current}/${CHAT_CONNECTION_CONSTANTS.MAX_RECONNECT_ATTEMPTS}`
    })

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
    }

    // Only reconnect if still not connected when the timer fires — avoids a
    // redundant connection attempt if the user reconnected through another path.
    reconnectTimeoutRef.current = setTimeout(() => {
      if (connectionStatusRef.current !== 'connected') {
        connectUserRef.current()
      }
    }, backoffTime)
  }, [telemetry, userDetails?.id])

  /**
   * Catch-all Stream Chat event handler.
   * - Keeps `unreadCount` in sync whenever any event carries a `total_unread_count`.
   * - Responds to `connection.changed`: triggers reconnect when the socket goes
   *   offline, resets the backoff counter when it comes back online.
   */
  const handleStreamEvents = useCallback(
    (event: Event) => {
      if (event.total_unread_count !== null && event.total_unread_count !== undefined) {
        updateState({ unreadCount: event.total_unread_count || 0 })
      }

      if (event.type === 'connection.changed') {
        if (!event.online) {
          telemetry.addBreadcrumb?.({
            category: 'chat',
            level: 'warning',
            message: 'Stream Chat connection went offline'
          })
          scheduleReconnect()
        } else {
          telemetry.addBreadcrumb?.({
            category: 'chat',
            level: 'info',
            message: 'Stream Chat connection restored'
          })
          resetReconnectAttempts()
        }
      }

      // Fired by the Stream SDK when it auto-reconnects after a brief network drop
      // (e.g. laptop sleep/wake). Without this handler, connectionStatus stays 'error'
      // even after Stream has already recovered.
      if (event.type === 'connection.recovered') {
        telemetry.addBreadcrumb?.({
          category: 'chat',
          level: 'info',
          message: 'Stream Chat connection recovered'
        })
        updateState({ connectionStatus: 'connected' })
        resetReconnectAttempts()
      }
    },
    [scheduleReconnect, resetReconnectAttempts, telemetry, updateState]
  )

  /**
   * Records periodic WebSocket health-check events as telemetry breadcrumbs.
   * Useful for diagnosing connectivity issues in production traces.
   */
  const handleHealthCheck = useCallback(
    (event: Event) => {
      telemetry.addBreadcrumb?.({
        category: 'chat',
        data: { connectionId: event.connection_id || 'unknown' },
        level: 'info',
        message: 'Health check completed'
      })
    },
    [telemetry]
  )

  /**
   * Handles `connection.error` events emitted by the Stream Chat WebSocket.
   * Delegates to `handleConnectionError` for telemetry reporting and then
   * schedules a reconnect attempt via exponential backoff.
   */
  const handleConnectionErrorEvent = useCallback(
    (event: Event & { error?: Error }) => {
      handleConnectionError('connection.error event', event.error)
      scheduleReconnect()
    },
    [handleConnectionError, scheduleReconnect]
  )

  /**
   * Initiates a Stream Chat WebSocket connection for the current user.
   *
   * Guard conditions (all must be true to proceed):
   * - No active `userConnection` in the ref (prevents duplicate connections)
   * - Current status is 'disconnected' or 'error' (not already connecting/connected)
   * - A valid `userChatToken` is available
   * - `userDetails.id` is resolved
   *
   * On success: updates state to 'connected' and resets the backoff counter.
   * On failure: reports through telemetry and schedules a reconnect.
   */
  const connectUser = useCallback(async (): Promise<void> => {
    const canConnect =
      !userConnectionRef.current &&
      (connectionStatusRef.current === 'disconnected' || connectionStatusRef.current === 'error') &&
      userChatToken &&
      userDetails?.id

    if (!canConnect) return

    try {
      updateState({ connectionStatus: 'connecting' })

      telemetry.addBreadcrumb?.({
        category: 'chat',
        data: { attemptNumber: reconnectAttempts.current + 1, userId: userDetails!.id },
        level: 'info',
        message: 'Attempting to connect user to Stream Chat'
      })

      const connection = await chatClient.connectUser(
        {
          id: userDetails!.id,
          image: userDetails!.photo || '',
          name: userDetails!.name || ''
        },
        userChatToken!
      )

      if (connection) {
        updateState({
          connectionStatus: 'connected',
          unreadCount: connection?.me?.total_unread_count || 0,
          userConnection: connection
        })
        resetReconnectAttempts()

        telemetry.addBreadcrumb?.({
          category: 'chat',
          data: {
            connectionId: String(connection?.connection_id || 'unknown'),
            userId: userDetails!.id
          },
          level: 'info',
          message: 'Successfully connected to Stream Chat'
        })
      }
    } catch (e) {
      telemetry.addBreadcrumb?.({
        category: 'chat',
        data: { error: 'ERR_NETWORK_CHANGED', userId: userDetails?.id },
        level: 'warning',
        message: 'Network changed detected during connection, will retry'
      })

      handleConnectionError('connectUser method', e)
      scheduleReconnect()
    }
  }, [
    userChatToken,
    userDetails,
    chatClient,
    handleConnectionError,
    scheduleReconnect,
    resetReconnectAttempts,
    telemetry,
    updateState
  ])

  connectUserRef.current = connectUser

  // Stable refs for values needed in the auto-connect effect
  const userChatTokenRef = useRef(userChatToken)
  userChatTokenRef.current = userChatToken

  const userDetailsIdRef = useRef(userDetails?.id)
  userDetailsIdRef.current = userDetails?.id

  /**
   * Explicitly disconnects the current user from Stream Chat.
   * Resets connection state and clears any pending reconnect timers.
   * Should be called on sign-out or workspace switch.
   */
  const disconnectUser = useCallback((): void => {
    try {
      telemetry.addBreadcrumb?.({
        category: 'chat',
        data: { userId: userDetails?.id },
        level: 'info',
        message: 'Disconnecting user from Stream Chat'
      })

      chatClient.disconnectUser()
      updateState({ connectionStatus: 'disconnected', userConnection: null })
      resetReconnectAttempts()
    } catch (e) {
      handleConnectionError('disconnectUser method', e)
    }
  }, [userDetails, chatClient, handleConnectionError, resetReconnectAttempts, telemetry, updateState])

  // Register event listeners once after connection is established, clean up on disconnect.
  // Keyed on `state.userConnection` so listeners are re-registered if the connection object
  // is replaced (e.g. after a successful reconnect).
  useEffect(() => {
    if (!state.userConnection) return

    chatClient.on(handleStreamEvents)
    chatClient.on('health.check', handleHealthCheck)
    chatClient.on('connection.error', handleConnectionErrorEvent)

    return () => {
      chatClient.off(handleStreamEvents)
      chatClient.off('health.check', handleHealthCheck)
      chatClient.off('connection.error', handleConnectionErrorEvent)
    }
  }, [state.userConnection, chatClient, handleStreamEvents, handleHealthCheck, handleConnectionErrorEvent])

  // Auto-connect when token and user details become available.
  // Uses refs to avoid re-running (and clearing reconnect timeouts) on every connectUser recreation.
  useEffect(() => {
    if (userChatTokenRef.current && userDetailsIdRef.current) {
      connectUserRef.current()
    }
  }, [userChatToken, userDetails?.id])

  // If auth token is cleared while ChatProvider is still mounted, force a Stream disconnect.
  useEffect(() => {
    if (!userChatToken && userConnectionRef.current) {
      disconnectUser()
    }
  }, [disconnectUser, userChatToken])

  // Clean up reconnection timers on unmount to prevent state updates on an unmounted component.
  useEffect(() => {
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }
    }
  }, [])

  return {
    ...state,
    connectUser,
    disconnectUser
  }
}
